import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { MetadataStep } from "@/components/submit/MetadataStep";
import { PublishedStep } from "@/components/submit/PublishedStep";
import { RiskScanStep } from "@/components/submit/RiskScanStep";
import { SubmitStepper } from "@/components/submit/SubmitStepper";
import { UploadStep } from "@/components/submit/UploadStep";
import { type SubmitMetadata, useSubmitSkill } from "@/hooks/use-submit-skill";
import { useSession } from "@/lib/auth-client";

export function SubmitFlow() {
  const navigate = useNavigate();
  const { data: session } = useSession();
  const submit = useSubmitSkill();

  const [step, setStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [metadata, setMetadata] = useState<SubmitMetadata | null>(null);
  const [slug, setSlug] = useState<string | null>(null);

  const submitterName = session?.user.name ?? "You";

  function handleScanComplete() {
    if (!file || !metadata) {
      setStep(1);
      return;
    }
    submit.mutate(
      { file, metadata },
      {
        onSuccess: (data) => {
          setSlug(data.slug);
          setStep(3);
        },
        onError: () => setStep(1),
      },
    );
  }

  function reset() {
    setFile(null);
    setMetadata(null);
    setSlug(null);
    setStep(0);
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-fg-link">
          Contribute
        </div>
        <h1 className="mt-1 font-display text-[30px] font-bold text-fg-1">Submit a Skill</h1>
        <p className="mt-2 max-w-2xl text-fg-2">
          Share a Claude skill with the internal community. Required metadata keeps every skill
          comparable — publishing is instant once the lightweight scan clears.
        </p>
      </div>

      <div className="rounded-[12px] border border-border-1 bg-bg-1 p-6">
        <SubmitStepper step={step} />
        <div className="mt-6">
          {step === 0 ? (
            <UploadStep file={file} onFile={setFile} onContinue={() => setStep(1)} />
          ) : null}
          {step === 1 ? (
            <MetadataStep
              defaultValues={metadata}
              submitterName={submitterName}
              onBack={() => setStep(0)}
              onSubmit={(v) => {
                setMetadata(v);
                setStep(2);
              }}
            />
          ) : null}
          {step === 2 ? (
            <RiskScanStep publishing={submit.isPending} onScanComplete={handleScanComplete} />
          ) : null}
          {step === 3 && slug ? (
            <PublishedStep
              title={metadata?.title ?? "Your skill"}
              onView={() => navigate({ to: "/skill/$id", params: { id: slug } })}
              onAnother={reset}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
